import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import axios from "axios";
import "./documentsInstallers.css";
import { pdfjs } from "react-pdf";
import { fetchDocumentProvider } from "../functions/Functions";
import UploadContractForm from "./DocumentUploadForm";
import SideMenu from "../../features/sideMenu/SideMenu";
import { itemsProviders } from "../../constants/providers";
import "./DocumentsProvider.responsive.css";
import {
  getTrackerProveedoresByEmpresa,
  updateTrackerProviderEvents,
} from "../functions/Notifications";
import CustomButton from "../../ui/button/Button";
import StepperModal from "../../features/stepperModal/StepperModal";
import LoaderDialog from "../../ui/loader/Loader";
import { formatEmpresa } from "../../utils/functionsFormat";
import Exportar from "../../assets/img/svg/providers/ExportarBlanco.svg";
import DescargaVerde from "../../assets/img/svg/users/descargarGreen.svg";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const DocumentsProvider = ({ API_URL, user }) => {
  const { empresaId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [pdfPreviews, setPdfPreviews] = useState({});
  const [company, setCompany] = useState(null);
  const [trackerData, setTrackerData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadCount, setDownloadCount] = useState(0);

  const handleUpdateEvent = async (estatus, reason, details) => {
    try {
      setIsLoading(true);

      await updateTrackerProviderEvents(
        API_URL,
        company.id,
        2,
        estatus,
        estatus === "error" ? reason : details,
        user.id,
        company.nombre,
        user.correo,
        user.nombre,
        company.email
      );

      if (estatus === "completado") {
        setTrackerData(null);
      }

      setTimeout(() => {
        setIsLoading(false);
      }, 1);
    } catch (error) {
      setIsLoading(false);
      console.error(
        "Error al actualizar el evento del tracker del proveedor:",
        error
      );
    }
  };

  const reloadDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/contratos-empresa`, {
        params: { empresaId },
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al recargar documentos:", error);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getDocFileName = (doc, companyName) => {
    const base =
      doc?.nombre ||
      doc?.tipoDocumento?.name ||
      `documento-${doc?.id || Math.random().toString(36).slice(2)}`;
    const empresa = (companyName || "empresa")
      .toString()
      .trim()
      .replace(/[^\w\-]+/g, "_");
    return `${empresa}__${base}.pdf`;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Último documento por tipo (siguiendo tu criterio de UploadContractForm: mayor fechaVencimiento)
  const getLatestDocsByType = (docs) => {
    const byType = {};
    for (const d of docs || []) {
      const tipo = d?.tipoDocumento?.name || "DESCONOCIDO";
      const prev = byType[tipo];

      if (!prev) {
        byType[tipo] = d;
        continue;
      }

      const dDate = d?.fechaVencimiento ? new Date(d.fechaVencimiento) : null;
      const pDate = prev?.fechaVencimiento
        ? new Date(prev.fechaVencimiento)
        : null;

      if (dDate && pDate) {
        if (dDate > pDate) byType[tipo] = d;
      } else if (dDate && !pDate) {
        byType[tipo] = d;
      } else if (!dDate && !pDate) {
        // desempate por updatedAt/createdAt/id si no hay fechas
        const da =
          new Date(d?.updatedAt || d?.createdAt || 0).getTime() ||
          Number(d.id) ||
          0;
        const pa =
          new Date(prev?.updatedAt || prev?.createdAt || 0).getTime() ||
          Number(prev.id) ||
          0;
        if (da > pa) byType[tipo] = d;
      }
    }
    return Object.values(byType);
  };

  // Memo de “actuales”
  const latestDocs = useMemo(() => getLatestDocsByType(documents), [documents]);

  // Handler: descarga solo los actuales (último por tipo)
  const handleDownloadAllCurrent = async () => {
    const disponibles = (latestDocs || []).filter((d) => !!d.link);
    if (disponibles.length === 0) {
      console.warn("No hay documentos actuales para descargar.");
      return;
    }

    setIsDownloading(true);
    setDownloadCount(0);

    try {
      for (const doc of disponibles) {
        try {
          const blob = await fetchDocumentProvider(API_URL, doc.id);
          const fileName = getDocFileName(
            doc,
            company?.razonSocial || company?.nombre
          );
          downloadBlob(blob, fileName);
          setDownloadCount((prev) => prev + 1);
          await new Promise((res) => setTimeout(res, 120)); // respiro para el navegador
        } catch (err) {
          console.error(`Error al descargar doc ${doc?.id}:`, err);
        }
      }
    } finally {
      setTimeout(() => setIsDownloading(false), 200);
    }
  };

  useEffect(() => {
    reloadDocuments();
    const fetchCompany = async () => {
      try {
        const [response, trackerResponse] = await Promise.all([
          axios.get(`${API_URL}/api/empresas/${empresaId}`, {
            withCredentials: true,
          }),
          getTrackerProveedoresByEmpresa(API_URL, empresaId),
        ]);
        setTrackerData(trackerResponse);
        setCompany(response.data);
      } catch (error) {
        console.error("Error al obtener empresa:", error);
      }
    };

    if (empresaId) fetchCompany();
  }, [API_URL, empresaId]);

  useEffect(() => {
    const generarPreviews = async () => {
      // Limpiar URLs anteriores
      Object.values(pdfPreviews).forEach((url) => URL.revokeObjectURL(url));
      setPdfPreviews({}); // Limpiar previews anteriores

      for (const doc of documents) {
        if (doc.link) {
          try {
            const blob = await fetchDocumentProvider(API_URL, doc.id);
            const url = URL.createObjectURL(blob);
            setPdfPreviews((prev) => ({ ...prev, [doc.id]: url }));
          } catch (err) {
            console.error("Error cargando previsualización", err);
          }
        }
      }
    };

    generarPreviews();
  }, [documents]);

  //const formatName = (fullName) => fullName?.toUpperCase() || "";
  const formatName = (fullName) => formatEmpresa(fullName);
  return (
    <div className="docs-layout" style={{ display: "flex", height: "100%" }}>
      <div className="docs-sidebar" style={{ width: "12rem" }}>
        <SideMenu items={itemsProviders} />
      </div>

      <div className="main-container">
        {company && (
          <>
            <div className="page-header">
              <p className="document-name">
                {" "}
                Documentos proveedor:{" "}
                {formatName(company.razonSocial || company.nombre || "Empresa")}
              </p>

              <CustomButton
                className="download-current-button"
                text={
                  isDownloading
                    ? `Descargando actuales... (${downloadCount}/${
                        (latestDocs || []).filter((d) => d.link).length
                      })`
                    : "Exportar todo"
                }
                color={"#FFFFFF"}
                border={"none"}
                background={isDownloading ? "#9aa7b1" : "#223D51"}
                fontStyle={"normal"}
                fontSize={"14px"}
                width={"172px"}
                height={"38px"}
                type={"button"}
                fontWeight={700}
                icon={true} // ← solo muestra el icono si hay archivo
                src={DescargaVerde}
                iconW="12px"
                iconM="5px"
                gap="5px"
                disabled={
                  isDownloading ||
                  (latestDocs || []).filter((d) => d.link).length === 0
                }
                onClick={handleDownloadAllCurrent}
              />
            </div>
          </>
        )}
        <UploadContractForm
          API_URL={API_URL}
          empresaId={empresaId}
          onUploadSuccess={reloadDocuments}
          documentosExistentes={documents}
          previewsExistentes={pdfPreviews}
        />

        {/* <div className="content-container">
          {isLoading ? (
            <div className="spinner">
              <Oval
                visible={true}
                height="80"
                width="80"
                color="#4fa94d"
                ariaLabel="oval-loading"
              />
            </div>
          ) : null}
        </div> */}
        <StepperModal
          onClose={() => setOpenModal(false)}
          open={openModal}
          title="Validación de documentos"
          onConfirm={handleUpdateEvent}
        />
        <div className="acciones-button-container">
          {/* {trackerData && trackerData.etapa === 2 && (
            <CustomButton
              className="notifications-button"
              text={"Acciones"}
              color={"#FFFFFF"}
              border={"none"}
              background={"#223D51"}
              fontStyle={"normal"}
              fontWeight={500}
              fontSize={"14px"}
              height={"28px"}
              type={"button"}
              onClick={() => setOpenModal(true)}
            />
          )} */}
        </div>
      </div>
      {isLoading && <LoaderDialog open={isLoading} />}
    </div>
  );
};

export default DocumentsProvider;
